/**
 * Field-scope registry — the single source of truth for which card/deck
 * fields replicate through linked study groups versus staying on a user's
 * own UserDo.
 *
 * Every write that flows through `sync.decks.deck(id).cards.*` is
 * partitioned against this table before leaving the client:
 *
 *   - `shared` fields pile up into a "shared patch" that propagates to
 *     every group listed in the deck's `linkedGroups`. Those groups
 *     broadcast the shared patch to other members, whose UserDos apply
 *     it to the linked local deck.
 *
 *   - `personal` fields never leave the user's UserDo. Even the deck's
 *     owner doesn't see another member's study state, starred flag,
 *     personal notes, etc.
 *
 * Extending the system (public tags, private quizzes, shared annotations,
 * per-member visibility rules) is a matter of declaring a new field with a
 * scope here. The sync engine does the rest. Do NOT branch on field names
 * elsewhere — the only place the shared/personal boundary is encoded is
 * this file.
 *
 * Invariants:
 *   - Every key on the referenced schema shape must appear in its
 *     scope map (including `id`). Partial coverage silently drops
 *     writes. The `partitionByScope` helper in
 *     `sync-engine/framework/field-scope-router.ts` throws on unknown keys to
 *     enforce this at runtime; CI should also type-check via
 *     `Record<keyof Shape, FieldScope>`.
 */

import type { SyncCardProps, SyncDeckMeta } from './shared-types';

/**
 * A field's replication scope.
 *
 *   - `identity`: present in BOTH authorities (UserDo + StudyGroupDo).
 *     Used for row addressing (`id`, `deckPaths`) and for fields the
 *     receive side needs in order to resolve the row regardless of
 *     which authority canonically owns it (`linkedGroups`,
 *     `originDeckId`, `originUserId`, `forkedFromDeckId`). Identity
 *     fields flow through the scope router into both halves of a
 *     partition and are accepted by the merge router regardless of
 *     `applyScope`.
 *
 *   - `shared`: canonical home is StudyGroupDo for linked decks.
 *     Travels with the SG fanout; the UserDo carries a snapshot for
 *     fast composed reads but the SG copy is authoritative.
 *
 *   - `personal`: canonical home is UserDo. NEVER reaches StudyGroupDo
 *     under any condition.
 */
export type FieldScope = 'identity' | 'shared' | 'personal';

/** Must cover every key on `SyncCardProps`. See file-level invariant. */
export type CardFieldScopeMap = Record<keyof SyncCardProps, FieldScope>;

/** Must cover every key on `SyncDeckMeta`. See file-level invariant. */
export type DeckFieldScopeMap = Record<keyof SyncDeckMeta, FieldScope>;

/**
 * Card-level scope classification.
 *
 * `shared`:
 *   - The card's identity (`id`) and the fields that describe what the
 *     card IS to every member: front, back, images, aiNotes, source.
 *   - Ordering + lifecycle metadata that must be globally consistent:
 *     `sortKey`, `createdTimeISO`, `deletedTimeISO`, `sharedUpdatedAtISO`
 *     (which replaces the undifferentiated `updatedAtISO`).
 *
 * `personal`:
 *   - Everything that describes how a member INTERACTS with the card:
 *     `studyState`, `actions`, `starred`, `tags`, `notes`, `chat`, and
 *     the paired `personalUpdatedAtISO`.
 *
 * The pre-Option-F `updatedAtISO` is intentionally absent — it conflated
 * shared and personal timestamps and made LWW ambiguous. Callers should
 * read `sharedUpdatedAtISO` or `personalUpdatedAtISO` depending on
 * intent, and `max(shared, personal)` for a "last touched" UI label.
 */
export const CARD_FIELD_SCOPES: CardFieldScopeMap = {
  // Identity: addresses the row in both authorities. Present in every
  // partition and accepted by every merge.
  id: 'identity',

  front: 'shared',
  back: 'shared',
  source: 'shared',
  aiNotes: 'shared',
  images: 'shared',
  sortKey: 'shared',
  createdTimeISO: 'shared',
  deletedTimeISO: 'shared',
  // Source-media timestamps (video/audio decks). Like `source`, they
  // describe what the card IS — every member sees the same moment.
  sourceTimeSec: 'shared',
  sourceTimeEndSec: 'shared',

  notes: 'personal',
  actions: 'personal',
  studyState: 'personal',
  chat: 'personal',
  tags: 'personal',
  starred: 'personal',
  // Progress-reset exemption stamp (see SyncCardProps.joinedDeckAtISO).
  joinedDeckAtISO: 'personal',
  // Undo tombstones (see SyncCardProps.undoneISO) — part of the history.
  undoneISO: 'personal',

  // Pre-Option-F timestamp. Retained as shared so any lingering writers
  // round-trip cleanly during the transition. Remove once every caller
  // has migrated to the split `sharedUpdatedAtISO` / `personalUpdatedAtISO`
  // pair below.
  updatedAtISO: 'shared',
  // Option-F per-scope timestamps. Each one is classified as the scope
  // it tracks — `sharedUpdatedAtISO` travels with shared-field edits so
  // remote LWW can compare like-for-like; `personalUpdatedAtISO` stays
  // personal.
  sharedUpdatedAtISO: 'shared',
  personalUpdatedAtISO: 'personal',
};

/** List of all `personal` keys. Used by the scope router for fast lookup. */
export const PERSONAL_CARD_FIELDS: ReadonlyArray<keyof SyncCardProps> = Object.entries(
  CARD_FIELD_SCOPES,
)
  .filter(([, scope]) => scope === 'personal')
  .map(([k]) => k as keyof SyncCardProps);

/** List of all `shared` keys (excludes `identity`). */
export const SHARED_CARD_FIELDS: ReadonlyArray<keyof SyncCardProps> = Object.entries(
  CARD_FIELD_SCOPES,
)
  .filter(([, scope]) => scope === 'shared')
  .map(([k]) => k as keyof SyncCardProps);

/** List of all `identity` keys — fields that appear in BOTH authority projections. */
export const IDENTITY_CARD_FIELDS: ReadonlyArray<keyof SyncCardProps> = Object.entries(
  CARD_FIELD_SCOPES,
)
  .filter(([, scope]) => scope === 'identity')
  .map(([k]) => k as keyof SyncCardProps);

/**
 * Deck-level scope classification (Phase 2).
 *
 * `shared`:
 *   - Identity, content metadata, and group-side organisation:
 *     `displayName`, `color`, `imageBackground`, `thumbnailText`,
 *     `languageSettings`, `editPermission`, `editors`, `addedByUserId`,
 *     `sharedAtISO`, `sharedUpdatedAtISO`, `sgFolderId`, `sgDeckSortKey`,
 *     `deletedTimeISO`, plus link metadata (`linkedGroups`, `originDeckId`,
 *     `originUserId`, `forkedFromDeckId`).
 *   - Identity-derived addressing (`id`, `deckPaths`) — every member of a
 *     shared deck addresses it the same way.
 *
 * `personal`:
 *   - Per-member organisation: `personalFolderId`, `personalDeckSortKey`,
 *     `personalUpdatedAtISO`.
 *   - Legacy single-purpose fields kept personal-scope so existing
 *     write paths continue to land in UserDo without migration:
 *     `folder`, `sortKey`, `orderIndex`, `addedTimeISO`, `updatedAtISO`.
 *
 * `languageSettings` is shared (content shape, used by AI/PDF/quiz).
 *
 * `deckPaths` is shared (path-derived addressing visible to all members).
 *
 * Lifecycle fields (`addedTimeISO`, `updatedAtISO`) are personal-scope:
 * they record when *this user* added the deck and last touched it locally,
 * not when the canonical SG copy was last updated.
 */
export const DECK_FIELD_SCOPES: DeckFieldScopeMap = {
  // Identity / addressing — present in BOTH authorities. Includes the
  // primary key (`id`), legacy addressing (`deckPaths`), and the link
  // metadata used to resolve a member-side deck to its owner / group
  // (`linkedGroups`, `originDeckId`, `originUserId`, `forkedFromDeckId`).
  // Mirrors the `RETAINED_IDENTITY_KEYS` set in
  // `flashka-web/party/server/shared/scope-strip.ts` — that retention
  // list becomes derivable from this scope map once the unification
  // lands fully.
  id: 'identity',
  deckPaths: 'identity',
  linkedGroups: 'identity',
  originDeckId: 'identity',
  originUserId: 'identity',
  forkedFromDeckId: 'identity',

  // Shared content metadata
  displayName: 'shared',
  imageBackground: 'shared',
  color: 'shared',
  thumbnailText: 'shared',
  languageSettings: 'shared',
  deletedTimeISO: 'shared',

  // Shared timestamps (Phase 2 split)
  sharedAtISO: 'shared',
  sharedUpdatedAtISO: 'shared',

  // PDF / source overview. Shared so every member of a linked deck sees
  // the same source (the owner uploaded it; members read it). Personal
  // scope would force re-upload per member, which defeats the cache.
  sources: 'shared',
  // Travels with `sources`: a member must not have the owner's removed
  // document restored under them by their own backfill.
  sourcesClearedAtISO: 'shared',
  // Removals of `sources` entries — shared with the list they prune.
  sourceTombstones: 'shared',
  // Which document was made first, and when — shared with the list it orders.
  sourcePrimaryStamps: 'shared',

  // The deck's notes and mind maps. Shared for the same reason as `sources`:
  // a linked deck's members read the same materials, and a note visible to
  // one member but not another is a deck that means two different things.
  materials: 'shared',

  // Shared group-side organisation (Phase 2)
  sgFolderId: 'shared',
  sgDeckSortKey: 'shared',
  editPermission: 'shared',
  editors: 'shared',
  addedByUserId: 'shared',

  // SGDeckProps-specific shared fields. Without these the SG-side
  // projection (`projectEntity`) would drop them — see the projection
  // contract in framework/projection.ts:58-64 ("Unknown key. Drop it
  // on the projection"). `deckName` is the routing-key the client
  // uses to construct the `__sg_{groupId}_{deckName}` mirror; `folderId`
  // is the SG-side folder organisation; `hideSource` toggles whether
  // members see the source PDF. All three are inherently shared —
  // every member sees the same value.
  deckName: 'shared',
  folderId: 'shared',
  hideSource: 'shared',

  // Personal organisation (Phase 2)
  personalFolderId: 'personal',
  personalDeckSortKey: 'personal',
  personalUpdatedAtISO: 'personal',

  // Legacy personal-scope (kept for in-flight writes; cleaned up by
  // the wipe-and-reseed at final cutover).
  folder: 'personal',
  sortKey: 'personal',
  orderIndex: 'personal',
  addedTimeISO: 'personal',
  updatedAtISO: 'personal',
  cardCount: 'personal',

  // Manual archive is inherently per-user: a deck shared in a study group
  // can't be manually archived (UI is gated), and even if the field were
  // somehow set on a shared deck, the archive state shouldn't propagate to
  // other members. Personal-scope keeps writes contained to UserDo.
  manualArchivedAtISO: 'personal',

  // "Exclude from To-do" is a study preference — what THIS user wants to be
  // reminded of. Another member of a shared deck keeps their own To-do.
  excludedFromTodo: 'personal',

  // Exam study plan (deck-shell Study tab). A user's own pacing toward
  // THEIR exam — another member of a shared deck has their own exam (or
  // none), so the plan must never fan out through the group.
  examPlan: 'personal',

  // Progress-reset floor. Resetting is the user's own history — a member's
  // reset of a shared deck must never reset anyone else's progress.
  progressResetAtISO: 'personal',

  // Server-only twin merge record (see SyncDeckMeta.sgMerged): this user's
  // own copies, never a group's business.
  sgMerged: 'personal',

  // Link-sharing (`/d/:code`). Personal-scope so a deck's public link-share
  // state NEVER fans out to study-group members — only the owner sees their
  // own `shareCode`/`linkSharedAtISO`/`shareOptions`. Same containment the
  // personal `cardCount` field already relies on.
  shareCode: 'personal',
  linkSharedAtISO: 'personal',
  shareOptions: 'personal',

  // Who shared the link this deck was imported from. Recipient-side and
  // per-user by nature (another member of the same shared deck imported
  // nothing), so `personal` keeps it out of every group fan-out.
  sharedByUserId: 'personal',
};

/** List of all `personal` deck keys. */
export const PERSONAL_DECK_FIELDS: ReadonlyArray<keyof SyncDeckMeta> = Object.entries(
  DECK_FIELD_SCOPES,
)
  .filter(([, scope]) => scope === 'personal')
  .map(([k]) => k as keyof SyncDeckMeta);

/** List of all `shared` deck keys (excludes `identity`). */
export const SHARED_DECK_FIELDS: ReadonlyArray<keyof SyncDeckMeta> = Object.entries(
  DECK_FIELD_SCOPES,
)
  .filter(([, scope]) => scope === 'shared')
  .map(([k]) => k as keyof SyncDeckMeta);

/** List of all `identity` deck keys — fields that appear in BOTH authority projections. */
export const IDENTITY_DECK_FIELDS: ReadonlyArray<keyof SyncDeckMeta> = Object.entries(
  DECK_FIELD_SCOPES,
)
  .filter(([, scope]) => scope === 'identity')
  .map(([k]) => k as keyof SyncDeckMeta);

// ---------------------------------------------------------------------------
// Derived field types (Phase 2)
// ---------------------------------------------------------------------------

/**
 * Compile-time partition of a record type by scope. Given a scope map,
 * `PickByScope<T, M, S>` is the subset of `T` whose keys are classified
 * as `S` in `M`. `S` may be a union (e.g. `'shared' | 'identity'`) to
 * pick everything that belongs in a given authority's projection.
 *
 * Used to derive `SharedCardFields`, `PersonalCardFields`,
 * `SharedDeckFields`, `PersonalDeckFields` without duplicating the
 * field lists in two places. Adding a new field + its scope entry
 * automatically updates the derived types.
 */
export type PickByScope<
  T,
  M extends Record<keyof T, FieldScope>,
  S extends FieldScope,
> = { [K in keyof T as M[K] extends S ? K : never]: T[K] };

/**
 * Shared card fields = strictly `shared` scope. Excludes identity —
 * use `SharedCardFieldsWithIdentity` when you need the full projection
 * that travels with a shared push (front/back/… plus `id`).
 */
export type SharedCardFields = PickByScope<SyncCardProps, CardFieldScopeMap, 'shared'>;
export type PersonalCardFields = PickByScope<
  SyncCardProps,
  CardFieldScopeMap,
  'personal'
>;
export type IdentityCardFields = PickByScope<
  SyncCardProps,
  CardFieldScopeMap,
  'identity'
>;
/** Full projection sent to StudyGroupDo on a card push (identity + shared). */
export type SharedCardFieldsWithIdentity = PickByScope<
  SyncCardProps,
  CardFieldScopeMap,
  'shared' | 'identity'
>;
/** Full projection sent to / stored at UserDo (identity + personal). */
export type PersonalCardFieldsWithIdentity = PickByScope<
  SyncCardProps,
  CardFieldScopeMap,
  'personal' | 'identity'
>;

export type SharedDeckFields = PickByScope<SyncDeckMeta, DeckFieldScopeMap, 'shared'>;
export type PersonalDeckFields = PickByScope<SyncDeckMeta, DeckFieldScopeMap, 'personal'>;
export type IdentityDeckFields = PickByScope<SyncDeckMeta, DeckFieldScopeMap, 'identity'>;
/** Full projection sent to StudyGroupDo on a deck-meta push (identity + shared). */
export type SharedDeckFieldsWithIdentity = PickByScope<
  SyncDeckMeta,
  DeckFieldScopeMap,
  'shared' | 'identity'
>;
/** Full projection sent to / stored at UserDo (identity + personal). */
export type PersonalDeckFieldsWithIdentity = PickByScope<
  SyncDeckMeta,
  DeckFieldScopeMap,
  'personal' | 'identity'
>;
